/**
 * ChatHeader component.
 *
 * Displays entity branding (logo + name) and a close button. Styled
 * with the entity's primary color as background.
 *
 * REQ-D-046 — when `allowExpand` is true, renders an in-header expand
 * / compact toggle that flips the panel's size state. The toggle is
 * hidden when the widget is in fullscreen mode (i.e. mobile-keyboard /
 * form-mode entry) because there is nowhere bigger to go.
 *
 * Layout shape (per EEC Figma + specs/entity-branding.md §2):
 *
 *   - Compact (48px tall, single-line): 24px circular bot avatar on the
 *     left, entity name only, no subtitle. Used when the panel size is
 *     "compact" so vertical real-estate is reserved for messages.
 *
 *   - Expanded (64px tall, two-line stack): 32px circular bot avatar
 *     left of a stacked title/subtitle pair. "Concierge" subtitle uses
 *     rgba(255,255,255,0.9) for ≥4.5:1 WCAG AA contrast on EEC's
 *     #E8651A primary background.
 *
 *   - Modal: same two-line treatment but the title block is centered
 *     so it reads as a "page" surface rather than a panel — matches the
 *     PDF modal mockups feel.
 */

import { CLOSE_ICON_SVG } from "./icons";
import type { ResolvedWidgetConfig, WidgetSize } from "../types";

/**
 * Inline SVG for the bot avatar circle. Minimal speech-bubble glyph
 * rendered in opaque white inside a translucent white-on-primary disc.
 * Kept inline to avoid an external asset fetch + survive Shadow DOM.
 */
const BOT_AVATAR_SVG =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M20 4H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3v3l4-3h9a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" fill="currentColor"/>' +
  "</svg>";

export interface ChatHeaderOptions {
  config: ResolvedWidgetConfig;
  onClose: () => void;
  /**
   * REQ-D-046 — invoked when the user clicks the expand/compact toggle.
   * Omitted (or never invoked) when the size state machine isn't active
   * (allowExpand=false).
   */
  onToggleSize?: () => void;
  /**
   * REQ-D-046 — initial size for the toggle's `aria-expanded` /
   * `aria-label` reflection. Defaults to the resolved config.
   */
  initialSize?: WidgetSize;
  /** The line under the title. Defaults to "Concierge". */
  subtitle?: string;
}

export class ChatHeader {
  private readonly element: HTMLElement;
  private readonly subtitle: HTMLElement | null;
  private readonly toggleBtn: HTMLButtonElement | null;
  private currentSize: WidgetSize;

  constructor(options: ChatHeaderOptions) {
    const { config, onClose, onToggleSize, initialSize, subtitle } = options;
    this.currentSize = initialSize ?? config.defaultSize;

    this.element = document.createElement("header");
    this.element.className = "hana-header";
    this.element.setAttribute("role", "banner");
    // REQ-D-046 — size class drives single-line (48px) vs two-line (64px)
    // header layout per the uiux critique.
    this.element.classList.add(`hana-header--${this.currentSize}`);
    // REQ-D-046 — mode class flips the title block to centered layout in
    // modal mode per the PDF mockups.
    this.element.classList.add(`hana-header--mode-${config.uiMode}`);

    // Bot avatar — circular disc with inset speech-bubble glyph. Sized
    // by CSS (24px compact / 32px expanded) so the header chrome height
    // determines visual weight.
    const avatar = document.createElement("span");
    avatar.className = "hana-header__avatar";
    avatar.setAttribute("role", "img");
    // Avatar represents the bot itself, not the entity — keep the bot
    // name stable across all entity brandings so screen-reader users
    // hear "HANA bot avatar" regardless of which clinic they're on.
    avatar.setAttribute("aria-label", "HANA bot avatar");
    avatar.innerHTML = BOT_AVATAR_SVG;
    this.element.appendChild(avatar);

    // Optional brand logo — kept as a hidden img preserved for embedders
    // that set `branding.logoUrl`. Inserted alongside the avatar so the
    // existing tests asserting `.hana-header__logo` still find the node.
    const logo = document.createElement("img");
    logo.className = "hana-header__logo";
    logo.alt = `${config.branding.displayName} logo`;
    if (config.branding.logoUrl) {
      logo.src = config.branding.logoUrl;
    } else {
      logo.classList.add("hana-header__logo--hidden");
    }
    logo.addEventListener("error", () => {
      logo.classList.add("hana-header__logo--hidden");
    });
    this.element.appendChild(logo);

    // Title block (title + optional subtitle stacked vertically when
    // expanded, single line when compact).
    const titleBlock = document.createElement("div");
    titleBlock.className = "hana-header__titles";
    const title = document.createElement("span");
    title.className = "hana-header__title";
    title.textContent = config.branding.displayName;
    titleBlock.appendChild(title);

    // REQ-D-046 — "Concierge" subtitle. Per spec/entity-branding.md § 2
    // (EEC), the static role descriptor. Hidden via CSS when size is
    // compact so the 48px header stays single-line.
    this.subtitle = document.createElement("span");
    this.subtitle.className = "hana-header__subtitle";
    this.subtitle.textContent = subtitle ?? "Concierge";
    titleBlock.appendChild(this.subtitle);

    this.element.appendChild(titleBlock);

    // REQ-D-046 — expand / compact toggle. Rendered only when the host
    // explicitly opted in via allowExpand=true; we keep the DOM lean
    // for the legacy snippet path. Aria reflection mirrors size state.
    if (config.allowExpand && onToggleSize) {
      this.toggleBtn = document.createElement("button");
      this.toggleBtn.className = "hana-header__toggle";
      this.toggleBtn.setAttribute("type", "button");
      this.applyToggleAria();
      const icon = document.createElement("span");
      icon.className = "hana-header__toggle-icon";
      // Inline SVG kept minimal — two stacked chevrons that rotate via
      // CSS depending on size class so we don't need two icon assets.
      icon.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<path d="M4 6l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M4 10l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        "</svg>";
      this.toggleBtn.appendChild(icon);
      this.toggleBtn.addEventListener("click", () => onToggleSize());
      this.element.appendChild(this.toggleBtn);
    } else {
      this.toggleBtn = null;
    }

    // Close button
    const closeBtn = document.createElement("button");
    closeBtn.className = "hana-header__close";
    closeBtn.setAttribute("aria-label", "Close chat");
    closeBtn.setAttribute("type", "button");

    const closeIcon = document.createElement("span");
    closeIcon.className = "hana-header__close-icon";
    closeIcon.innerHTML = CLOSE_ICON_SVG;
    closeBtn.appendChild(closeIcon);

    closeBtn.addEventListener("click", onClose);
    this.element.appendChild(closeBtn);
  }

  /**
   * REQ-D-046 — surface size changes from the parent so header chrome
   * (single-line vs two-line, aria reflection, toggle visibility) stays
   * in sync with the panel's resolved state.
   */
  setSize(size: WidgetSize): void {
    if (this.currentSize === size) return;
    this.element.classList.remove(`hana-header--${this.currentSize}`);
    this.element.classList.add(`hana-header--${size}`);
    this.currentSize = size;
    this.applyToggleAria();
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private applyToggleAria(): void {
    if (!this.toggleBtn) return;
    // Hide the toggle entirely when fullscreen — the user reached the
    // ceiling, there's nowhere bigger to go.
    if (this.currentSize === "fullscreen") {
      this.toggleBtn.classList.add("hana-header__toggle--hidden");
      this.toggleBtn.setAttribute("aria-hidden", "true");
      this.toggleBtn.setAttribute("tabindex", "-1");
      return;
    }
    this.toggleBtn.classList.remove("hana-header__toggle--hidden");
    this.toggleBtn.removeAttribute("aria-hidden");
    this.toggleBtn.removeAttribute("tabindex");
    const isExpanded = this.currentSize === "expanded";
    this.toggleBtn.setAttribute("aria-expanded", String(isExpanded));
    this.toggleBtn.setAttribute(
      "aria-label",
      isExpanded ? "Compact chat" : "Expand chat",
    );
  }
}
